/**
 * AIScheduler - Centralized AI Move Timing and Execution
 *
 * Handles all AI move scheduling across different game modes:
 * - Watch Mode: AI vs AI with 2000ms delays for viewing
 * - NN Test Mode: AI plays Dark only with 10ms delays for fast testing
 * - Play vs AI / Tutorial: AI plays Dark only with 10ms delays, handles bonus turns
 *
 * This manager removes AI control logic from GameContext (Phase 3 - SOC refactoring)
 */

import { ZodiacEngine } from "../zodiacEngine";
import { GameState, Mode, State } from "../types";

export type AIMode = 'watch' | 'test' | 'playVsAI' | 'off';

interface AISchedulerConfig {
  mode: AIMode;
  delay: number;        // Delay in ms before AI makes a move
  maxRetries: number;   // Maximum retry attempts
}

export class AIScheduler {
  private engine: ZodiacEngine;
  private config: AISchedulerConfig | null = null;
  private isThinking: boolean = false;
  private previousTurn: boolean | null = null;
  private retryCount: number = 0;
  private moveTimeoutId: NodeJS.Timeout | null = null;

  // Callbacks for UI updates
  private thinkingStateCallback: ((thinking: boolean) => void) | null = null;
  private moveCompleteCallback: ((success: boolean) => void) | null = null;
  private errorCallback: ((error: Error) => void) | null = null;

  constructor(engine: ZodiacEngine) {
    this.engine = engine;
  }

  /**
   * Start AI scheduling in Watch mode (AI vs AI)
   * Both sides played by AI with 2s delays for viewing
   */
  startWatchMode(delayMs: number = 2000): void {
    this.config = {
      mode: 'watch',
      delay: delayMs,
      maxRetries: 3
    };
    this.reset();
    this.startMonitoring();
  }

  /**
   * Start AI scheduling in Test mode (NN Test)
   * AI plays Dark only with fast 10ms delays for testing
   */
  startTestMode(delayMs: number = 10): void {
    this.config = {
      mode: 'test',
      delay: delayMs,
      maxRetries: 3
    };
    this.reset();
    this.startMonitoring();
  }

  /**
   * Start AI scheduling in Play vs AI mode
   * AI plays Dark only with 10ms delays, handles bonus turns
   */
  startPlayVsAIMode(delayMs: number = 10): void {
    this.config = {
      mode: 'playVsAI',
      delay: delayMs,
      maxRetries: 3
    };
    this.reset();
    this.startMonitoring();
  }

  /**
   * Stop AI scheduling
   */
  stop(): void {
    this.config = null;
    this.clearMoveTimeout();
    this.reset();
  }

  /**
   * Check if AI is currently thinking
   */
  isAIThinking(): boolean {
    return this.isThinking;
  }

  /**
   * Reset internal state
   */
  private reset(): void {
    this.isThinking = false;
    this.previousTurn = null;
    this.retryCount = 0;
    this.clearMoveTimeout();
  }

  /**
   * Clear pending move timeout
   */
  private clearMoveTimeout(): void {
    if (this.moveTimeoutId) {
      clearTimeout(this.moveTimeoutId);
      this.moveTimeoutId = null;
    }
  }

  /**
   * Start monitoring game state for AI move triggers
   * This is called periodically to check if AI should make a move
   */
  private startMonitoring(): void {
    // We'll be triggered by external calls to checkAndExecuteAIMove()
    // This method just initializes the monitoring state
  }

  /**
   * Check if AI should make a move and execute it
   * Called externally (e.g., from GameContext when game state changes)
   */
  async checkAndExecuteAIMove(gameState: GameState): Promise<void> {
    if (!this.config) {
      return; // Not active
    }

    // Tutorial restart detection: Reset previousTurn when turnCount goes to 0
    if (gameState.turnCount === 0 && this.previousTurn !== null) {
      this.previousTurn = null;
      this.retryCount = 0;
      return;
    }

    // Only run in Playing state
    if (gameState.state !== State.Playing) {
      return;
    }

    // Check mode-specific conditions
    if (!this.shouldMakeMove(gameState)) {
      return;
    }

    // Schedule the AI move
    await this.scheduleMove(gameState);
  }

  /**
   * Determine if AI should make a move based on mode and game state
   */
  private shouldMakeMove(gameState: GameState): boolean {
    if (!this.config) return false;

    // Don't make a move if already thinking
    if (this.isThinking) {
      return false;
    }

    const currentTurn = gameState.isLightTurn;

    switch (this.config.mode) {
      case 'watch':
        // Watch mode: AI plays both sides
        // Skip if there's a bonus turn active
        if (gameState.activeBonusTurnPieceId) {
          return false;
        }
        // Move if turn changed or we're retrying
        return this.previousTurn !== currentTurn || this.retryCount > 0;

      case 'test':
        // Test mode: AI plays Dark only
        // Skip Light turns
        if (currentTurn) {
          return false;
        }
        // Skip if there's a bonus turn active
        if (gameState.activeBonusTurnPieceId) {
          return false;
        }
        // Move if turn changed or we're retrying
        return this.previousTurn !== currentTurn || this.retryCount > 0;

      case 'playVsAI':
        // Play vs AI: AI plays Dark only
        // Skip Light turns
        if (currentTurn) {
          return false;
        }
        // Allow bonus turns for Dark (AI) pieces
        // Skip only if bonus turn is for Light (human)
        if (gameState.activeBonusTurnPieceId && currentTurn) {
          return false;
        }
        // Check if it's a new turn or a Dark bonus turn
        const isBonusTurnMove: boolean = !!(gameState.activeBonusTurnPieceId && !currentTurn);
        const isNewTurn: boolean = !currentTurn && this.previousTurn !== currentTurn;
        return isNewTurn || isBonusTurnMove;

      default:
        return false;
    }
  }

  /**
   * Schedule and execute an AI move
   */
  private async scheduleMove(gameState: GameState): Promise<void> {
    if (!this.config) return;

    // Set thinking state
    this.setThinking(true);

    // Clear any pending timeout
    this.clearMoveTimeout();

    // Schedule the move with configured delay
    this.moveTimeoutId = setTimeout(async () => {
      try {
        const moveResult = await this.engine.makeAIMove();

        if (moveResult) {
          // Move succeeded
          const newState = this.engine.getGameState();
          this.previousTurn = newState.isLightTurn;
          this.retryCount = 0;

          // Notify callback
          if (this.moveCompleteCallback) {
            this.moveCompleteCallback(true);
          }
        } else {
          // Move failed - retry if under limit
          this.retryCount++;

          if (this.retryCount < (this.config?.maxRetries || 3)) {
            // Will retry on next state change
          } else {
          }

          // Notify callback
          if (this.moveCompleteCallback) {
            this.moveCompleteCallback(false);
          }
        }
      } catch (error) {
        this.retryCount++;

        // Notify error callback
        if (this.errorCallback && error instanceof Error) {
          this.errorCallback(error);
        }
      } finally {
        // Clear thinking state
        this.setThinking(false);
      }
    }, this.config.delay);
  }

  /**
   * Set thinking state and notify callback
   */
  private setThinking(thinking: boolean): void {
    this.isThinking = thinking;
    if (this.thinkingStateCallback) {
      this.thinkingStateCallback(thinking);
    }
  }

  /**
   * Subscribe to thinking state changes
   * Returns unsubscribe function
   */
  onThinkingStateChange(callback: (thinking: boolean) => void): () => void {
    this.thinkingStateCallback = callback;
    return () => {
      this.thinkingStateCallback = null;
    };
  }

  /**
   * Subscribe to move completion events
   * Returns unsubscribe function
   */
  onMoveComplete(callback: (success: boolean) => void): () => void {
    this.moveCompleteCallback = callback;
    return () => {
      this.moveCompleteCallback = null;
    };
  }

  /**
   * Subscribe to error events
   * Returns unsubscribe function
   */
  onError(callback: (error: Error) => void): () => void {
    this.errorCallback = callback;
    return () => {
      this.errorCallback = null;
    };
  }
}
